import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchCountries,
  createCountry,
  updateCountry,
  deleteCountry,
} from "../../features/auth/store/countrySlice";
import { fetchCurrencies } from "../../features/auth/store/currencySlice";
import { Country } from "../../features/auth/store/types/country-types";
import { Currency } from "../../features/auth/store/types/currency-types";
import { CountryTable } from "./component";
import { ModalContainer } from "../../components/PageComponents";
import { OutlineButton, GradientButton } from "../../components/forms";
import { VALID_COUNTRY_CODES_MAP } from "./constants";

const CountriesManagementPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const items: Country[] = useSelector(
    (state: any) => state.countries?.items ?? [],
  );
  const currencies: Currency[] = useSelector(
    (state: any) => state.currencies?.items ?? [],
  );
  const loading: boolean = useSelector(
    (state: any) => state.countries?.loading ?? false,
  );
  const saving: boolean = useSelector(
    (state: any) => state.countries?.saving ?? false,
  );
  const error: string | null = useSelector(
    (state: any) => state.countries?.error ?? null,
  );

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Country | null>(null);
  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [defaultCurrencyId, setDefaultCurrencyId] = useState<string>("");
  const [search, setSearch] = useState("");
  const [codeError, setCodeError] = useState("");
  const [image, setImage] = useState<File | null>(null);

  useEffect(() => {
    dispatch(fetchCountries({ search }));
  }, [dispatch, search]);

  useEffect(() => {
    if (!currencies || currencies.length === 0) {
      dispatch(fetchCurrencies({ perPage: 200 }));
    }
  }, [dispatch, currencies]);

  useEffect(() => {
    if (editing) {
      setName(editing.name);
      setSymbol(editing.symbol);
      setDefaultCurrencyId(String(editing.default_currency_id));
    } else {
      setName("");
      setSymbol("");
      setDefaultCurrencyId("");
    }
  }, [editing, formOpen]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (item: Country) => {
    setEditing(item);
    setFormOpen(true);
  };

  const handleDelete = (item: Country) => {
    if (!window.confirm(`Delete country ${item.name}?`)) return;
    dispatch(deleteCountry(item.id));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const removeImage = () => setImage(null);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const countryCode = symbol.trim().toUpperCase();
    const isValidCode = /^[A-Z]{2}$/.test(countryCode);
    if (!isValidCode) {
      setCodeError("Invalid code format. Must be two letters (e.g., NG, US).");
      return;
    }
    if (!VALID_COUNTRY_CODES_MAP[symbol]) {
      setCodeError(
        `The code "${symbol}" is not a recognized ISO country code.`,
      );
      return;
    }
    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("symbol", countryCode);
    formData.append(
      "default_currency_id",
      parseInt(defaultCurrencyId, 10).toString(),
    );
    if (image) {
      formData.append("image", image);
    }

    if (editing) {
      dispatch(updateCountry({ id: editing.id, data: formData }));
    } else {
      dispatch(createCountry(formData));
    }
    setFormOpen(false);
  };

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      ),
    [items],
  );

  const CloseIcon = () => {
    return (
      <button
        onClick={() => setFormOpen(false)}
        className="text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-white transition-colors"
        type="button"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    );
  };

  return (
    <div className="p-4 sm:p-6 dark:bg-zinc-950 min-h-screen">
      {/* Header Container */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-semibold dark:text-white">Countries</h2>
          <p className="text-xs text-gray-500 dark:text-zinc-400">
            Manage countries and their default currencies for product
            availability.
          </p>
        </div>

        {/* Actions Row */}
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full sm:w-64 border rounded px-3 py-2 text-sm dark:bg-zinc-900 dark:border-zinc-700 dark:text-white dark:placeholder-zinc-500"
          />
          <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
            <OutlineButton onClick={openCreate} content="New Country" />
            <GradientButton
              onClick={() => dispatch(fetchCountries({ search }))}
              content="Refresh"
            />
          </div>
        </div>
      </div>

      <div>
        <CountryTable
          items={sortedItems}
          loading={loading}
          onEdit={openEdit}
          onDelete={handleDelete}
        />
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 mt-2">
            {error}
          </div>
        )}
      </div>

      {formOpen && (
        <ModalContainer
          title={editing ? "Edit Country" : "Create Country"}
          closeIcon={<CloseIcon />}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold mb-1 block dark:text-zinc-300">
                Name
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-2 border rounded dark:bg-zinc-800 dark:border-zinc-700 dark:text-white"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block dark:text-zinc-300">
                Country Code
              </label>
              <input
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                className="w-full p-2 border rounded dark:bg-zinc-800 dark:border-zinc-700 dark:text-white"
                maxLength={10}
                required
                placeholder="Two character country code (e.g NG, US)"
              />
              {codeError && (
                <div className="text-sm text-red-600 dark:text-red-400 mt-2">
                  {codeError}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block dark:text-zinc-300">
                Default Currency
              </label>
              <select
                value={defaultCurrencyId}
                onChange={(e) => setDefaultCurrencyId(e.target.value)}
                className="w-full p-2 border rounded dark:bg-zinc-800 dark:border-zinc-700 dark:text-white"
                required
              >
                <option value="" className="dark:bg-zinc-900">
                  Select currency
                </option>
                {currencies.map((c) => (
                  <option key={c.id} value={c.id} className="dark:bg-zinc-900">
                    {c.name} ({c.symbol})
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-xs font-semibold dark:text-zinc-300">
                Image
              </label>
              {/* Added px-4 py-6 dynamic reduction to keep child elements from overflowing bounds */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-8 text-center dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-white">
                <input
                  type="file"
                  id="providerImageUpload"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label
                  htmlFor="providerImageUpload"
                  className="flex flex-col items-center justify-center cursor-pointer w-full"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mb-2"
                  >
                    <path
                      d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                      stroke="currentColor"
                      className="text-gray-400 dark:text-zinc-500"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 8L12 3L7 8"
                      stroke="currentColor"
                      className="text-gray-400 dark:text-zinc-500"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 3V15"
                      stroke="currentColor"
                      className="text-gray-400 dark:text-zinc-500"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {/* Cleaned layout text scaling for mobile breakpoints */}
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-zinc-400 break-words max-w-full block px-2">
                    Upload from computer
                  </span>
                </label>
              </div>

              {image && (
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="relative rounded-md overflow-hidden h-20 ring-1 ring-gray-200 dark:ring-zinc-700">
                    <img
                      src={URL.createObjectURL(image)}
                      alt="provider"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-1 right-1 bg-white dark:bg-zinc-900 rounded-full p-1"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 6L18 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <OutlineButton
                onClick={() => setFormOpen(false)}
                content="Cancel"
              />

              <GradientButton
                type="submit"
                disabled={saving}
                content={editing ? "Update" : "Create"}
              />
            </div>
          </form>
        </ModalContainer>
      )}
    </div>
  );
};

export default CountriesManagementPage;
